import { useState, type FormEvent } from 'react'
import { subscribeNewsletter } from '@/lib/api'
import { validateNewsletter } from '@/lib/forms'

/**
 * Newsletter block. Sign-ups are saved
 * to the `newsletter_subscribers` table through /api/newsletter.
 */
export default function NewsletterForm({ tone = 'light', source = 'site' }: { tone?: 'light' | 'dark'; source?: string }) {
  const [email, setEmail] = useState('')
  const [website, setWebsite] = useState('')
  const [state, setState] = useState<'idle' | 'sending' | 'done'>('idle')
  const [error, setError] = useState<string | null>(null)
  const btn = tone === 'light' ? 'btn-white' : 'btn-black'

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    const v = validateNewsletter({ email })
    if (!v.ok) {
      setError(v.errors.email ?? 'Please enter a valid email address.')
      return
    }
    setError(null)
    setState('sending')
    try {
      await subscribeNewsletter(v.value.email, source, website)
      setState('done')
    } catch {
      setState('idle')
      setError('Sorry, that did not go through. Please try again in a moment.')
    }
  }

  return (
    <div className="mx-auto max-w-2xl text-center">
      <h2>Newsletter</h2>
      <p className="mt-3">Sign up to receive news and updates.</p>
      {state === 'done' ? (
        <p className="mt-8 text-xl" role="status">
          Thank you!
        </p>
      ) : (
        <form onSubmit={onSubmit} className="mt-8" noValidate>
          <div className="flex flex-col gap-4 sm:flex-row">
            <label className="flex-1">
              <span className="sr-only">Email Address</span>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Email Address"
                autoComplete="email"
                aria-invalid={error ? true : undefined}
                className="field-boxed h-full text-lg"
              />
            </label>
            {/* Honeypot: hidden from people, filled by bots. */}
            <input
              type="text"
              name="website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="absolute -left-[9999px] h-px w-px opacity-0"
            />
            <button type="submit" className={`btn ${btn}`} disabled={state === 'sending'}>
              {state === 'sending' ? 'Signing Up…' : 'Sign Up'}
            </button>
          </div>
          <p className="mt-3 min-h-6 text-sm" role="alert">
            {error}
          </p>
        </form>
      )}
    </div>
  )
}
